/**
 * Memory 聊天页面
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/store/authStore'
import { useChatStore } from '../store/chatStore'
import { memoryAdminApi } from '../services/memory-admin-api'
import { useSSE } from '../hooks/useSSE'
import type { Session, Message } from '../types/memory'
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Zap,
  X,
  CheckCircle,
  Loader2,
  Wifi,
  WifiOff,
} from 'lucide-react'

import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import ChatArea from '../components/ChatArea'
import ExtractionPanel from '../components/ExtractionPanel'
import GraphPanel from '../components/graph/GraphPanel'
import { 
  loadNotificationsFromStorage, 
  saveNotificationsToStorage, 
  addNotificationToStorage 
} from '@/shared/components/notification'
import type { Notification } from '@/shared/components/notification'

export default function Chat() {
  const navigate = useNavigate()
  const { user, isAuthenticated, checkAuth, isLoading } = useAuthStore()
  const { 
    sessionId, 
    isExtracting, 
    extractionResult, 
    setSessionId, 
    setMessages, 
    setExtractionResult, 
    setIsExtracting 
  } = useChatStore()

  const [projectId, setProjectId] = useState('default')
  const [isVerifying, setIsVerifying] = useState(true)
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true)
  const [graphPanelOpen, setGraphPanelOpen] = useState(false)
  const [showExtractionToast, setShowExtractionToast] = useState(false)
  const [viewingUserId, setViewingUserId] = useState<string | undefined>()
  const [sseExtractionStatus, setSseExtractionStatus] = useState<'idle' | 'in_progress' | 'completed' | 'failed'>('idle')
  const [sseExtractionMessage, setSseExtractionMessage] = useState('')
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [currentSessionStatus, setCurrentSessionStatus] = useState<Session['status'] | undefined>()
  
  // 是否有重新提取权限
  const canReExtract = user?.role === 'developer' || user?.role === 'admin' || user?.role === 'super_admin'
  
  // 初始化加载通知
  useEffect(() => {
    setNotifications(loadNotificationsFromStorage('memory_service_notifications'))
  }, [])

  // 用户主键统一用 id（参考 user-id-naming-convention.md）
  const currentUserId = user?.id
  
  // SSE 实时通知
  const { isConnected: sseConnected, isExtracting: sseIsExtracting } = useSSE(
    currentUserId || null,
    {
      autoConnect: true,
      onExtractionStarted: (data) => {
        console.log('[Chat] SSE: Extraction started', data)
        setSseExtractionStatus('in_progress')
        setSseExtractionMessage('正在提取记忆...')
        setShowExtractionToast(true)
        setIsExtracting(true)
      },
      onExtractionProgress: (data) => {
        console.log('[Chat] SSE: Extraction progress', data)
        setSseExtractionMessage(data.message || `提取进度: ${data.progress}%`)
      },
      onExtractionCompleted: async (data) => {
        console.log('[Chat] SSE: Extraction completed', data)
        setSseExtractionStatus('completed')
        setSseExtractionMessage(`提取完成: ${data.entities_count || 0} 个实体, ${data.relationships_count || 0} 个关系`)
        
        // 添加通知
        const notification = addNotificationToStorage({
          type: 'extraction_completed',
          title: '提取完成',
          message: `提取了 ${data.entities_count || 0} 个实体，${data.relationships_count || 0} 个关系`,
          metadata: { sessionId: data.session_id || '' },
        }, 'memory_service_notifications')
        setNotifications(prev => [notification, ...prev])
        
        // 从后端拉取本次提取的实体和关系（使用 Memory Admin API）
        try {
          const currentUser = useAuthStore.getState().user
          // 用户主键统一用 id
          const userId = currentUser?.id
          
          if (userId && data.session_id) {
            // 使用 memoryAdminApi.getSessionExtraction 获取提取结果
            const extractionStatus = await memoryAdminApi.getSessionExtraction(data.session_id, userId)
            
            if (extractionStatus) {
              console.log('[Chat] Extraction status:', extractionStatus)
              // 更新 chatStore 的 extractionResult
              setExtractionResult({
                session_id: data.session_id,
                entities: extractionStatus.entities || [],
                relationships: extractionStatus.relationships || [],
                extracted_at: extractionStatus.extracted_at || new Date().toISOString(),
              })
            }
          }
        } catch (error) {
          console.error('[Chat] Failed to fetch extraction status:', error)
          // 即使获取详细信息失败，也设置基本信息（来自 SSE 事件）
          setExtractionResult({
            session_id: data.session_id || '',
            entities: [],
            relationships: [],
            entities_count: data.entities_count || 0,
            relationships_count: data.relationships_count || 0,
            extracted_at: new Date().toISOString(),
          })
        }
        
        setIsExtracting(false)
        
        // 5秒后隐藏
        setTimeout(() => {
          setShowExtractionToast(false)
          setSseExtractionStatus('idle')
        }, 5000)
      },
      onExtractionFailed: (data) => {
        console.log('[Chat] SSE: Extraction failed', data)
        setSseExtractionStatus('failed')
        setSseExtractionMessage(`提取失败: ${data.error || '未知错误'}`)
        
        setIsExtracting(false)
        
        const notification = addNotificationToStorage({
          type: 'extraction_failed',
          title: '提取失败',
          message: data.error || '未知错误',
          metadata: { sessionId: data.session_id || '' },
        }, 'memory_service_notifications')
        setNotifications(prev => [notification, ...prev])
        
        setTimeout(() => {
          setShowExtractionToast(false)
          setSseExtractionStatus('idle')
        }, 5000)
      },
    }
  )

  // 检查认证状态
  useEffect(() => {
    const verify = async () => {
      if (!isAuthenticated) {
        navigate('/login')
        return
      }

      const valid = await checkAuth()
      if (!valid) {
        navigate('/login')
        return
      }

      // 检查用户状态
      if (user?.status === 'pending') {
        navigate('/pending')
        return
      }
      if (user?.status === 'rejected' || user?.status === 'disabled') {
        alert('您的账号已被禁用或拒绝，请联系管理员')
        navigate('/login')
        return
      }

      setIsVerifying(false)
    }

    verify()
  }, [isAuthenticated, navigate, checkAuth, user])

  // 监听提取状态变化
  useEffect(() => {
    if (isExtracting) {
      setShowExtractionToast(true)
    }
  }, [isExtracting])

  // 提取完成后自动隐藏 toast
  useEffect(() => {
    if (extractionResult && !isExtracting) {
      const timer = setTimeout(() => {
        setShowExtractionToast(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [extractionResult, isExtracting])

  // 重新提取回调
  const handleReExtract = useCallback((sessionId: string) => {
    console.log('[Chat] Re-extract triggered for session:', sessionId)
  }, [])

  // 选择会话时加载消息和提取结果
  const handleSessionSelect = useCallback(
    async (selectedSessionId: string) => {
      if (selectedSessionId === sessionId) return

      // 用户主键统一用 id
      const userId = user?.id
      if (!userId) return

      console.log('[Chat] 加载会话:', selectedSessionId)

      // 使用 Promise.allSettled 替代 Promise.all，可以分别处理每个请求的成功/失败
      const [messagesResult, sessionResult] = await Promise.allSettled([
        memoryAdminApi.getSessionMessages(selectedSessionId, userId),
        memoryAdminApi.getSession(selectedSessionId, userId),
      ])

      // 分别检查每个请求的结果
      if (messagesResult.status === 'rejected') {
        console.error('[Chat] 获取会话消息失败:', JSON.stringify(messagesResult.reason, null, 2))
      }
      if (sessionResult.status === 'rejected') {
        console.error('[Chat] 获取会话详情失败:', JSON.stringify(sessionResult.reason, null, 2))
      }

      // 处理消息结果
      if (messagesResult.status === 'fulfilled') {
        const messagesRes = messagesResult.value
        setSessionId(selectedSessionId)

        const formattedMessages: Message[] = messagesRes.messages.map((msg) => ({
          id: msg.id,
          session_id: selectedSessionId,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || new Date().toISOString(),
        }))
        setMessages(formattedMessages)
        console.log('[Chat] 消息加载成功:', formattedMessages.length, '条')
      } else {
        // 消息加载失败，清空消息
        setMessages([])
      }

      // 处理会话详情结果
      if (sessionResult.status === 'fulfilled') {
        const session = sessionResult.value
        setViewingUserId(session.user_id)
        setCurrentSessionStatus(session.status)
        console.log('[Chat] 会话详情加载成功, status:', session.status)

        // 如果会话已完成提取，查询提取结果
        if (session.status === 'completed') {
          try {
            const extractionResult = await memoryAdminApi.getSessionExtraction(selectedSessionId, userId)
            console.log('[Chat] 提取结果:', extractionResult)
            setExtractionResult({
              session_id: selectedSessionId,
              entities: extractionResult.entities || [],
              relationships: extractionResult.relationships || [],
              extracted_at: extractionResult.extracted_at || new Date().toISOString(),
            })
          } catch (extractError) {
            console.error('[Chat] 获取提取结果失败:', JSON.stringify(extractError, null, 2))
            setExtractionResult(null)
          }
        } else {
          // 会话未完成提取，清空提取结果
          setExtractionResult(null)
        }
      } else {
        // 会话详情加载失败，但如果消息加载成功，仍然可以显示消息
        // 只是无法判断提取状态，清空提取结果
        setExtractionResult(null)
      }
    },
    [sessionId, setSessionId, setMessages, setExtractionResult, user]
  )

  // 处理通知点击
  const handleNotificationClick = useCallback((notification: Notification) => {
    const sessionId = notification.metadata?.sessionId as string | undefined
    if (sessionId) {
      handleSessionSelect(sessionId)
    }
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      )
      saveNotificationsToStorage(updated, 'memory_service_notifications')
      return updated
    })
  }, [handleSessionSelect])
  
  const handleClearNotifications = useCallback(() => {
    setNotifications([])
    saveNotificationsToStorage([], 'memory_service_notifications')
  }, [])
  
  const handleMarkAllRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }))
      saveNotificationsToStorage(updated, 'memory_service_notifications')
      return updated
    })
  }, [])

  if (isLoading || isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent border-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      <Header
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onRetry={handleReExtract}
        onClearNotifications={handleClearNotifications}
        onMarkAllRead={handleMarkAllRead}
      />

      <div className="flex-1 flex overflow-hidden relative">
        {/* 左侧边栏 */}
        <div
          className={`transition-all duration-300 ease-in-out ${leftSidebarOpen ? 'w-80' : 'w-0'} overflow-hidden`}
        >
          <Sidebar
            projectId={projectId}
            onProjectIdChange={setProjectId}
            onSessionSelect={handleSessionSelect}
            currentSessionId={sessionId || undefined}
            onReExtract={handleReExtract}
          />
        </div>

        {/* 左侧边栏切换按钮 */}
        <button
          onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 rounded-r-xl transition-all hover:opacity-80 bg-card border-r border-t border-b border-border shadow-lg"
          style={{
            left: leftSidebarOpen ? '320px' : '0',
          }}
          title={leftSidebarOpen ? '收起侧边栏' : '展开侧边栏'}
        >
          {leftSidebarOpen ? (
            <PanelLeftClose className="w-4 h-4 text-muted-foreground" />
          ) : (
            <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {/* 主聊天区 */}
        <ChatArea
          projectId={projectId}
          viewingUserId={viewingUserId}
          sessionStatus={currentSessionStatus}
          onReExtract={() => sessionId && handleReExtract(sessionId)}
          canReExtract={canReExtract}
        />

        {/* 右侧边栏切换按钮 */}
        <button
          onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
          className="absolute top-1/2 -translate-y-1/2 z-20 p-2 rounded-l-xl transition-all hover:opacity-80 bg-card border-l border-t border-b border-border shadow-lg"
          style={{
            right: rightSidebarOpen ? '288px' : '0',
          }}
          title={rightSidebarOpen ? '收起面板' : '展开面板'}
        >
          {rightSidebarOpen ? (
            <PanelRightClose className="w-4 h-4 text-muted-foreground" />
          ) : (
            <PanelRightOpen className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {/* 右侧边栏 */}
        <div
          className={`transition-all duration-300 ease-in-out ${rightSidebarOpen ? 'w-72' : 'w-0'} overflow-hidden`}
        >
          <ExtractionPanel userId={viewingUserId || currentUserId} projectId={projectId} />
        </div>
      </div>

      {/* SSE 连接状态指示器 */}
      <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-card border border-border shadow-lg backdrop-blur-sm">
        {sseConnected ? (
          <>
            <Wifi className="w-3 h-3 text-green-500" />
            <span className="text-muted-foreground">实时连接</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">离线</span>
          </>
        )}
      </div>

      {/* 提取状态 Toast */}
      {showExtractionToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl bg-card border border-border backdrop-blur-sm">
            {sseExtractionStatus === 'in_progress' || sseIsExtracting || isExtracting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                <span className="text-sm text-foreground">
                  {sseExtractionMessage || '正在提取记忆...'}
                </span>
              </>
            ) : sseExtractionStatus === 'completed' || extractionResult ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-foreground">
                  {sseExtractionMessage || `提取完成：${extractionResult?.entities?.length || 0} 实体，${extractionResult?.relationships?.length || 0} 关系`}
                </span>
              </>
            ) : sseExtractionStatus === 'failed' ? (
              <>
                <X className="w-5 h-5 text-red-500" />
                <span className="text-sm text-slate-100">
                  {sseExtractionMessage || '提取失败'}
                </span>
              </>
            ) : null}
            <button
              onClick={() => setShowExtractionToast(false)}
              className="ml-2 hover:opacity-80 text-slate-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 知识图谱按钮 */}
      <button
        onClick={() => setGraphPanelOpen(true)}
        className="fixed right-6 bottom-6 z-40 w-14 h-14 rounded-2xl flex items-center justify-center transition-transform hover:scale-105 bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/40"
        title="查看知识图谱"
      >
        <Zap className="w-6 h-6 text-white" />
      </button>

      {/* 知识图谱面板 */}
      <GraphPanel 
        isOpen={graphPanelOpen} 
        onClose={() => setGraphPanelOpen(false)} 
        viewingUserId={viewingUserId || currentUserId}
        projectId={projectId}
      />
    </div>
  )
}

